import * as React from 'react'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'

import {
    updateContextSettings,
    buildContextSettingsBody,
    saveContextVersion,
} from '../../hooks/useApi'
import { useSnackbar } from '../Snackbar'
import {
    getCurrentContext,
    getContextOption,
    flushProjectNotesPendingSave,
    emitEvent,
    trackEvent,
} from '../LeftMenuMigration/legacyBridge'

// Lemmatizer language options, in the order shown in the dropdown.
// Values are the language codes the backend expects.
const LANGUAGE_OPTIONS = [
    { value: 'AUTO', label: 'automatic' },
    { value: 'EN', label: 'english' },
    { value: 'AR', label: 'arabic' },
    { value: 'CN', label: 'chinese' },
    { value: 'NL', label: 'dutch' },
    { value: 'FR', label: 'french' },
    { value: 'DE', label: 'german' },
    { value: 'HE', label: 'hebrew' },
    { value: 'IT', label: 'italian' },
    { value: 'JP', label: 'japanese' },
    { value: 'KO', label: 'korean' },
    { value: 'NO', label: 'norwegian' },
    { value: 'FA', label: 'persian' },
    { value: 'PT', label: 'portugese' },
    { value: 'RU', label: 'russian' },
    { value: 'SP', label: 'spanish' },
    { value: 'VP', label: 'swedish' },
    { value: 'ZZ', label: 'do not lemmatize' },
]

const KNOWLEDGE_GRAPH_OPTIONS = [
    { value: 'WORDS_AND_ENTITIES', label: 'words and entities' },
    { value: 'PRIORITIZE_ENTITIES', label: 'prioritize tagged entities' },
    { value: 'ONLY_ENTITIES', label: 'only tagged entities' },
]

const PART_OF_SPEECH_OPTIONS = [
    { value: 'WORDS_IF_NO_HASHTAGS', label: 'prioritize hashtags' },
    { value: 'HASHTAGS_AND_WORDS', label: 'hashtags and words' },
    { value: 'HASHTAGS_ONLY', label: 'only hashtags' },
]

const WIKI_LINKS_OPTIONS = [
    { value: 'PROCESS_AS_HASHTAGS', label: 'as hashtags' },
    { value: 'PROCESS_AS_MENTIONS', label: 'as mentions' },
    {
        value: 'PROCESS_AS_HASHTAGS_IGNORE_THE_REST',
        label: 'prioritize over text',
    },
    { value: 'EXCLUDE', label: 'exclude from graph' },
    { value: 'IGNORE_BRACKETS', label: 'as normal words' },
]

const MENTIONS_OPTIONS = [
    { value: 'CONNECT_TO_ALL_CONCEPTS', label: 'connect to all' },
    { value: 'CONNECT_AND_REMOVE_THE_SIGN', label: 'connect, remove the sign' },
    {
        value: 'CONNECT_TO_CONCEPTS_ONLY',
        label: 'override all connections',
    },
    { value: 'EXCLUDE', label: 'excluded from the graph' },
]

const CATEGORIES_OPTIONS = [
    { value: 'false', label: "don't show on graph" },
    { value: 'true', label: 'show as mentions' },
]

// Higher than the per-user cap in routes/settings.js (600): a per-graph
// override is a deliberate choice, but bigger graphs make rendering and
// analytics noticeably slower.
const MAX_NODES_LIMIT = 2000
const DEFAULT_MAX_NODES = 150

/**
 * The per-graph node limit is persisted inside the context's default
 * graph version (revisionUrl): drawer links append those params to the
 * graph URL, and routes/graphs.js falls back to them server-side when
 * the request URL doesn't specify `maxnodes`. Read the current value
 * from there.
 */
function readDefaultVersionMaxNodes() {
    const defaultRevisionUrl = getContextOption('default') || ''
    return new URLSearchParams(defaultRevisionUrl).get('maxnodes') || ''
}

const showSnackbarSelector = state => state.showSnackbar

/**
 * Derive the high-level "knowledge graph type" from the two underlying
 * processing settings. Mirrors the legacy logic in LeftMenuLogic.ejs
 * (search for `partOfSpeechToProcess == 'HASHTAGS_AND_WORDS'`).
 *
 * `PROCESS_AS_HASHTAGS_IGNORE_THE_REST` for wiki links wins regardless
 * of part-of-speech because the legacy UI surfaced it as ONLY_ENTITIES.
 */
function deriveKnowledgeGraphType(partOfSpeech, doubleSquareBrackets) {
    if (doubleSquareBrackets === 'PROCESS_AS_HASHTAGS_IGNORE_THE_REST') {
        return 'ONLY_ENTITIES'
    }
    if (partOfSpeech === 'HASHTAGS_ONLY') return 'ONLY_ENTITIES'
    if (partOfSpeech === 'WORDS_IF_NO_HASHTAGS') return 'PRIORITIZE_ENTITIES'
    return 'WORDS_AND_ENTITIES'
}

/**
 * Read the synonyms textarea from the legacy `#mergednodesinfo` element.
 * userFactory.setDictionary() pre-populates it with `key:value\n` pairs;
 * regenerating that string from `getContextOption('synonyms')` would
 * duplicate non-trivial parsing logic, so we read it directly.
 */
function readLegacySynonymsTextarea() {
    if (typeof document === 'undefined') return ''
    const el = document.getElementById('mergednodesinfo')
    return el ? el.value || '' : ''
}

/**
 * Coerce a context option (which may be a real boolean, the string "true"/"false",
 * or undefined) into a real boolean for checkbox state.
 */
function toBool(value) {
    return value === true || value === 'true' || value === 1 || value === '1'
}

/**
 * Graph Language & Processing dialog.
 *
 * Hosts every field that used to live in the legacy `#languageProcessingData`
 * panel: lemmatizer language, hashtag lemmatization, knowledge-graph type,
 * stopwords, plus the advanced section (part-of-speech, wiki links, mentions,
 * categories, synonyms).
 *
 * Save flow:
 *   1. Flush any pending project-notes auto-save (so the reload doesn't drop it).
 *   2. POST `/context/settings` with the full current settings, all
 *      managed fields overridden — `buildContextSettingsBody` round-trips
 *      the legacy form so any field we DON'T manage stays intact.
 *   3. If the per-graph node limit changed, POST `/context/version` to
 *      persist it inside the default graph version's revisionUrl (see
 *      readDefaultVersionMaxNodes for how it's applied on load).
 *   4. Snackbar + hard reload — these settings change which tokens become
 *      graph nodes, so the rendered graph would be stale otherwise.
 */
export default function LanguageSettingsDialog({ open, onClose }) {
    const showSnackbar = useSnackbar(showSnackbarSelector)

    const initialValues = React.useMemo(() => {
        const partOfSpeech =
            getContextOption('partOfSpeechToProcess') || 'WORDS_IF_NO_HASHTAGS'
        const doubleSquareBrackets =
            getContextOption('doubleSquarebracketsProcessing') ||
            'PROCESS_AS_HASHTAGS'
        return {
            contextLanguage: getContextOption('language') || 'AUTO',
            lemmatizeHashtags: toBool(getContextOption('lemmatizeHashtags')),
            knowledgeGraphType: deriveKnowledgeGraphType(
                partOfSpeech,
                doubleSquareBrackets
            ),
            contextStopwords: getContextOption('stopwords') || '',
            partOfSpeechToProcess: partOfSpeech,
            doubleSquarebracketsProcessing: doubleSquareBrackets,
            mentionsProcessing:
                getContextOption('mentionsProcessing') ||
                'CONNECT_TO_ALL_CONCEPTS',
            categoriesAsMentions: toBool(
                getContextOption('categoriesAsMentions')
            ),
            synonymWords: readLegacySynonymsTextarea(),
            maxNodes: readDefaultVersionMaxNodes(),
        }
        // Re-seed every time the dialog opens so values reflect the
        // current backend state (e.g. after a previous save + reload, or
        // a context switch).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const [values, setValues] = React.useState(initialValues)
    const [advancedOpen, setAdvancedOpen] = React.useState(false)
    const [saving, setSaving] = React.useState(false)

    React.useEffect(() => {
        if (open) {
            setValues(initialValues)
            setAdvancedOpen(false)
        }
    }, [open, initialValues])

    const updateField = (name, value) =>
        setValues(prev => ({ ...prev, [name]: value }))

    /**
     * Knowledge-graph-type changes cascade into the underlying
     * partOfSpeechToProcess + doubleSquarebracketsProcessing fields.
     * Mirrors the legacy `$('#knowledgeGraphType').change(...)` handler
     * in LeftMenuLogic.ejs so users get the same behavior here.
     */
    const handleKnowledgeGraphTypeChange = next => {
        setValues(prev => {
            const update = { ...prev, knowledgeGraphType: next }
            if (next === 'WORDS_AND_ENTITIES') {
                update.partOfSpeechToProcess = 'HASHTAGS_AND_WORDS'
                if (
                    prev.doubleSquarebracketsProcessing ===
                    'PROCESS_AS_HASHTAGS_IGNORE_THE_REST'
                ) {
                    update.doubleSquarebracketsProcessing =
                        'PROCESS_AS_HASHTAGS'
                }
            } else if (next === 'PRIORITIZE_ENTITIES') {
                update.partOfSpeechToProcess = 'WORDS_IF_NO_HASHTAGS'
                if (
                    prev.doubleSquarebracketsProcessing ===
                    'PROCESS_AS_HASHTAGS_IGNORE_THE_REST'
                ) {
                    update.doubleSquarebracketsProcessing =
                        'PROCESS_AS_HASHTAGS'
                }
            } else if (next === 'ONLY_ENTITIES') {
                if (
                    prev.doubleSquarebracketsProcessing !==
                    'PROCESS_AS_HASHTAGS_IGNORE_THE_REST'
                ) {
                    update.partOfSpeechToProcess = 'HASHTAGS_ONLY'
                    update.doubleSquarebracketsProcessing =
                        'PROCESS_AS_HASHTAGS'
                }
            }
            return update
        })
    }

    const buildOverrides = state => ({
        contextLanguage: state.contextLanguage,
        lemmatizeHashtags: state.lemmatizeHashtags ? 'true' : 'false',
        contextStopwords: state.contextStopwords,
        partOfSpeechToProcess: state.partOfSpeechToProcess,
        doubleSquarebracketsProcessing: state.doubleSquarebracketsProcessing,
        mentionsProcessing: state.mentionsProcessing,
        categoriesAsMentions: state.categoriesAsMentions ? 'true' : 'false',
        synonymWords: state.synonymWords,
    })

    /**
     * Match the legacy `#unmergeall` button: confirm, clear the
     * `dictionary` localStorage entry, then save with empty
     * synonyms. The legacy implementation also auto-submitted the
     * form, so users saw an immediate reload — match that here.
     */
    const handleUnmergeAll = () => {
        if (
            !window.confirm(
                'Are you OK to proceed and to erase irrevocably all the synonym associations you created for this graph to merge the nodes?'
            )
        ) {
            return
        }
        try {
            const localstoreRaw = localStorage.getItem('dictionary')
            const localstore = localstoreRaw ? JSON.parse(localstoreRaw) : {}
            localstore[location.host + location.pathname] = ''
            localStorage.setItem('dictionary', JSON.stringify(localstore))
        } catch (e) {
            console.warn('[LanguageSettingsDialog] dictionary clear failed:', e)
        }
        const cleared = { ...values, synonymWords: '' }
        setValues(cleared)
        // Save with cleared synonyms — pass the new state explicitly so
        // we don't race the setState batch.
        saveOverrides(buildOverrides(cleared), cleared)
    }

    const saveOverrides = async (overrides, state) => {
        const contextName = getCurrentContext()
        if (!contextName) {
            showSnackbar('error', 'No graph context to update.')
            return
        }
        const maxNodesInput = String(state.maxNodes ?? '').trim()
        if (maxNodesInput) {
            const parsed = Number(maxNodesInput)
            if (
                !Number.isInteger(parsed) ||
                parsed < 1 ||
                parsed > MAX_NODES_LIMIT
            ) {
                showSnackbar(
                    'error',
                    `Nodes to show must be a whole number between 1 and ${MAX_NODES_LIMIT} — bigger graphs get too slow to render.`
                )
                return
            }
        }
        const maxNodesChanged = maxNodesInput !== initialValues.maxNodes
        setSaving(true)
        try {
            await flushProjectNotesPendingSave()
            // Round-trip the full current settings state so we don't
            // accidentally clear anything the form happens to hold that
            // we don't surface here. Backend overwrites the whole
            // textProcessingSettings object with what the body contains.
            const body = buildContextSettingsBody(overrides)
            await updateContextSettings(contextName, body)
            if (maxNodesChanged) {
                // Persist the node limit in the default graph version,
                // merged into its existing params (chat / AI-idea graphs
                // keep their mode there) so we don't clobber them.
                const versionParams = new URLSearchParams(
                    getContextOption('default') || ''
                )
                if (maxNodesInput) {
                    versionParams.set('maxnodes', maxNodesInput)
                } else {
                    versionParams.delete('maxnodes')
                }
                // The endpoint rejects an empty saveurl; an explicit
                // empty maxnodes is harmless on load (falsy query param,
                // so the account default applies).
                const saveUrl = versionParams.toString() || 'maxnodes='
                // Infrasonic rejects duplicate revision names per context,
                // so each save needs a unique one.
                const versionName = `settings ${new Date()
                    .toISOString()
                    .slice(0, 19)
                    .replace('T', ' ')}`
                await saveContextVersion(contextName, {
                    versionName,
                    saveUrl,
                    isDefault: true,
                })
            }
            emitEvent('onStatementsAction', {
                action: 'edit_text_processing_settings',
                data: `contextLanguage=${overrides.contextLanguage}`,
            })
            trackEvent('edit_text_processing_settings')
            showSnackbar(
                'success',
                'Settings updated. Reloading the graph...'
            )
            // Hard reload required: changing language / stopwords /
            // synonyms / wiki-link handling mutates which tokens become
            // graph nodes, so the rendered graph is stale. When the node
            // limit changed, reload with the URL adjusted so the new
            // limit is visible immediately (a bare reload wouldn't pick
            // up the freshly saved default version).
            setTimeout(() => {
                if (!maxNodesChanged) {
                    window.location.reload()
                    return
                }
                const reloadUrl = new URL(window.location.href)
                if (maxNodesInput) {
                    reloadUrl.searchParams.set('maxnodes', maxNodesInput)
                } else {
                    reloadUrl.searchParams.delete('maxnodes')
                }
                window.location.href = reloadUrl.toString()
            }, 1500)
        } catch (err) {
            console.error('[LanguageSettingsDialog] save failed:', err)
            showSnackbar('error', `Failed to save: ${err.message}`)
            setSaving(false)
        }
    }

    const handleSave = () => saveOverrides(buildOverrides(values), values)

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            scroll="paper"
        >
            <DialogTitle sx={{ pr: 6 }}>
                Graph Language & Processing
                <IconButton
                    aria-label="close-language-dialog"
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme => theme.palette.text.secondary,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <DialogContentText sx={{ mb: 2 }}>
                    Specify the settings for the text-to-network conversion
                    algorithm for this graph.
                </DialogContentText>

                <Stack spacing={2.5}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="language-select-label">
                            Lemmatizer language
                        </InputLabel>
                        <Select
                            labelId="language-select-label"
                            id="language-select"
                            value={values.contextLanguage}
                            label="Lemmatizer language"
                            onChange={e =>
                                updateField('contextLanguage', e.target.value)
                            }
                            disabled={saving}
                            inputProps={{ 'data-testid': 'language-select' }}
                        >
                            {LANGUAGE_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Every word becomes its lemma (bricks → brick).
                            Pick your language for precise results, or "do
                            not lemmatize" to keep words as-is.
                        </Typography>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={values.lemmatizeHashtags}
                                onChange={e =>
                                    updateField(
                                        'lemmatizeHashtags',
                                        e.target.checked
                                    )
                                }
                                disabled={saving}
                            />
                        }
                        label="Lemmatize #hashtags and [[entities]]"
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel id="kg-type-label">Graph shows</InputLabel>
                        <Select
                            labelId="kg-type-label"
                            id="kg-type-select"
                            value={values.knowledgeGraphType}
                            label="Graph shows"
                            onChange={e =>
                                handleKnowledgeGraphTypeChange(e.target.value)
                            }
                            disabled={saving}
                        >
                            {KNOWLEDGE_GRAPH_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            By default we show words and entities tagged via
                            #hashtags or [[wiki links]]. Switch to entities-
                            only for an ontology view.
                        </Typography>
                    </FormControl>

                    <TextField
                        id="stopwords-input"
                        label="Words to hide"
                        value={values.contextStopwords}
                        onChange={e =>
                            updateField('contextStopwords', e.target.value)
                        }
                        disabled={saving}
                        multiline
                        minRows={3}
                        maxRows={6}
                        fullWidth
                        size="small"
                        placeholder="is,the,as,to,in"
                        helperText={
                            <>
                                Comma-separated stopwords to exclude from the
                                graph, in addition to your{' '}
                                <Link
                                    href="/settings#stopwords"
                                    target="_blank"
                                    rel="noopener"
                                    underline="hover"
                                >
                                    global stopwords list
                                </Link>
                                . Prefix with <code>-</code> to protect a word
                                (e.g. <code>-saas,-HIS</code>).
                            </>
                        }
                    />

                    <TextField
                        id="maxnodes-input"
                        label="Nodes to show"
                        type="number"
                        value={values.maxNodes}
                        onChange={e => updateField('maxNodes', e.target.value)}
                        disabled={saving}
                        fullWidth
                        size="small"
                        placeholder={String(DEFAULT_MAX_NODES)}
                        inputProps={{
                            min: 1,
                            max: MAX_NODES_LIMIT,
                            'data-testid': 'maxnodes-input',
                        }}
                        helperText={`How many of the most relevant nodes to display when this graph loads. Saved as this graph's default view. Leave empty to use your account default (${DEFAULT_MAX_NODES}). Max ${MAX_NODES_LIMIT} — bigger graphs are slower to render.`}
                    />

                    <Divider />

                    <Button
                        onClick={() => setAdvancedOpen(o => !o)}
                        endIcon={
                            advancedOpen ? <ExpandLess /> : <ExpandMore />
                        }
                        sx={{
                            justifyContent: 'space-between',
                            textTransform: 'none',
                        }}
                        disabled={saving}
                    >
                        Advanced text processing options
                    </Button>

                    <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
                        <Stack spacing={2.5} sx={{ pt: 1 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="pos-label">
                                    Process words
                                </InputLabel>
                                <Select
                                    labelId="pos-label"
                                    value={values.partOfSpeechToProcess}
                                    label="Process words"
                                    onChange={e =>
                                        updateField(
                                            'partOfSpeechToProcess',
                                            e.target.value
                                        )
                                    }
                                    disabled={saving}
                                >
                                    {PART_OF_SPEECH_OPTIONS.map(opt => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel id="wiki-label">
                                    Process [[wiki links]]
                                </InputLabel>
                                <Select
                                    labelId="wiki-label"
                                    value={values.doubleSquarebracketsProcessing}
                                    label="Process [[wiki links]]"
                                    onChange={e =>
                                        updateField(
                                            'doubleSquarebracketsProcessing',
                                            e.target.value
                                        )
                                    }
                                    disabled={saving}
                                >
                                    {WIKI_LINKS_OPTIONS.map(opt => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel id="mentions-label">
                                    Mentions @
                                </InputLabel>
                                <Select
                                    labelId="mentions-label"
                                    value={values.mentionsProcessing}
                                    label="Mentions @"
                                    onChange={e =>
                                        updateField(
                                            'mentionsProcessing',
                                            e.target.value
                                        )
                                    }
                                    disabled={saving}
                                >
                                    {MENTIONS_OPTIONS.map(opt => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel id="cats-label">
                                    Categories and tags
                                </InputLabel>
                                <Select
                                    labelId="cats-label"
                                    value={
                                        values.categoriesAsMentions
                                            ? 'true'
                                            : 'false'
                                    }
                                    label="Categories and tags"
                                    onChange={e =>
                                        updateField(
                                            'categoriesAsMentions',
                                            e.target.value === 'true'
                                        )
                                    }
                                    disabled={saving}
                                >
                                    {CATEGORIES_OPTIONS.map(opt => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                id="synonyms-input"
                                label="Synonym nodes"
                                value={values.synonymWords}
                                onChange={e =>
                                    updateField('synonymWords', e.target.value)
                                }
                                disabled={saving}
                                multiline
                                minRows={3}
                                maxRows={8}
                                fullWidth
                                size="small"
                                placeholder={
                                    'machine:machine learning\nlearning:machine learning'
                                }
                                helperText={
                                    <>
                                        Merge words into one node. One pair per
                                        line as <code>word:replacement</code>.
                                        See your{' '}
                                        <Link
                                            href="/settings#synonyms"
                                            target="_blank"
                                            rel="noopener"
                                            underline="hover"
                                        >
                                            global synonyms list
                                        </Link>
                                        .
                                    </>
                                }
                            />
                            <Button
                                onClick={handleUnmergeAll}
                                disabled={saving}
                                size="small"
                                color="inherit"
                                sx={{
                                    alignSelf: 'flex-start',
                                    textTransform: 'none',
                                }}
                            >
                                Unmerge all
                            </Button>
                        </Stack>
                    </Collapse>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    aria-label="save-language-settings"
                >
                    {saving ? 'Saving…' : 'Save & reload'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
